import { Check, StickyNote } from "lucide-react";
import type {
  CrmRecordDetail,
  CrmRecordTypeFieldName,
  CrmRecordTypeFields,
} from "@/lib/api/crm/masters";
import { formatRecordFieldText, recordFieldLabels, recordFieldValue, type RecordFieldGroup } from "../recordFields";
import { TraderName } from "./TraderName";

type RecordFieldValue = CrmRecordTypeFields[CrmRecordTypeFieldName];

function EmptyValue() {
  return <span className="text-gray-300">—</span>;
}

function FieldValue({ field, value }: { field: CrmRecordTypeFieldName; value: RecordFieldValue }) {
  if (value === undefined || value === null || value === "") return <EmptyValue />;

  if (field === "trader_id" && typeof value === "string") return <TraderName traderId={value} />;

  if (Array.isArray(value)) {
    if (value.length === 0) return <EmptyValue />;
    return (
      <div className="flex flex-wrap gap-1.5">
        {value.map((item) => (
          <span key={item} className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
            {item}
          </span>
        ))}
      </div>
    );
  }

  if (typeof value === "boolean") {
    return value ? (
      <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
        <Check className="h-3.5 w-3.5" />
        Yes
      </span>
    ) : (
      <span className="text-gray-500">No</span>
    );
  }

  if (typeof value === "number") return <>{value.toLocaleString()}</>;

  return <span className="whitespace-pre-wrap break-words">{formatRecordFieldText(field, value)}</span>;
}

export function RecordNotes({ notes }: { notes: string }) {
  return (
    <div className="flex gap-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
      <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <p className="whitespace-pre-wrap break-words">{notes}</p>
    </div>
  );
}

export function RecordFieldGroups({
  record,
  groups,
}: {
  record: CrmRecordDetail;
  groups: RecordFieldGroup[];
}) {
  return (
    <>
      {groups.map((group) => (
        <section key={group.title}>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">{group.title}</h4>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 2xl:grid-cols-3">
            {group.fields.map((field) => (
              <div key={field} className="min-w-0">
                <dt className="text-xs font-medium text-gray-500">{recordFieldLabels[field]}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <FieldValue field={field} value={recordFieldValue(field, record)} />
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </>
  );
}
